import PlacesAutocomplete from '@c/forms/controls/PlacesAutocomplete';
import PageSection from '@c/listing/shared/PageSection';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { useLoadScript } from '@react-google-maps/api';
import { useCreateListing } from '../_context';
import { CreateListingSection } from './CreateListingSection';
import { GoogleIcon } from 'i/google';
import { ProductDocument } from 'models/product';
import { useEffect } from 'react';
const libraries = ['places'] as ('places' | 'drawing' | 'geometry')[];

export default function CarsLocation() {
  const { form } = useCreateListing();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
    libraries,
  });

  const locationDisplay = form.watch('location_display');
  const location = form.watch('location');

  // Listings saved before `location_display` existed only carry coordinates;
  // reverse-geocode them so the field still pre-fills on edit.
  useEffect(() => {
    if (locationDisplay || !location?.latitude || !location?.longitude) return;
    let cancelled = false;
    fetch(`/api/geocode?lat=${location.latitude}&lng=${location.longitude}`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && typeof data?.address === 'string' && data.address) {
          form.setValue('location_display', data.address);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [locationDisplay, location?.latitude, location?.longitude]);

  const handleSelect = ({
    lat,
    lng,
    address,
    countryCode,
  }: {
    lat?: number;
    lng?: number;
    address: string;
    countryCode?: string;
  }) => {
    const hasLatLng = lat && lng;
    if (hasLatLng) {
      form.setValue('location', {
        longitude: +lng.toFixed(2),
        latitude: +lat.toFixed(2),
      });
    }
    form.setValue('location_display', address);
    if (countryCode) {
      // Google geocode short_name is an ISO 3166-1 alpha-2 code
      form.setValue(
        'country_code',
        countryCode as ProductDocument['country_code']
      );
    }
  };

  return (
    <CreateListingSection title="Location & Contact Info">
      <div className="mt-16 flex w-full flex-col items-start justify-between gap-16 lg:flex-row">
        <div className="flex flex-col gap-10 w-full">
          <div className="flex w-full items-center justify-between rounded-[2rem] bg-brand-lightest-gray p-4 ">
            {isLoaded ? (
              <PlacesAutocomplete
                requireLatLng
                defaultValue={locationDisplay ?? ''}
                setSelected={handleSelect}
                className="bg-inherit px-0 !text-[1.8rem]"
                hideSearchIcon
                MXAuto={false}
              />
            ) : null}
            <MagnifyingGlassIcon className="mr-6 h-8 w-8 text-black" />
          </div>
          <div className="flex items-center gap-x-4">
            <span className="text-body-sm text-text-muted">Powered by</span>
            <GoogleIcon />
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <h3 className="text-body-sm font-semibold text-black">
            Exact location is never used on Motorsport Locker.
          </h3>
          <p className="text-body-sm font-medium text-text-muted">
            As privacy and safety is one of our top priorities, we use your
            location to give buyers an approximate idea of where your bike is
            located.
          </p>
        </div>
      </div>
    </CreateListingSection>
  );
}
